import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactElement,
} from "react";
import FactionHeroCarousel from "./FactionHeroCarousel";
import {
  type CarouselDragGateEvent,
  useCarouselAutoplayInView,
  useCarouselPointerDrag,
} from "./ImageCarousel";
import { FACTION_CAROUSEL_ORDER, FACTION_COPY, FACTION_HEROES } from "./worldFactionContent";
import { WORLD_STORY_PARAGRAPHS } from "./worldStoryContent";

const FACTION_OUTER_INTERVAL_MS = 30000;
const HERO_INTERVAL_MS = 10000;

const WorldOfDfianceSection = (): ReactElement => {
  const [worldTab, setWorldTab] = useState<"factions" | "story">("factions");
  const [factionIndex, setFactionIndex] = useState(0);
  const totalFactions = FACTION_CAROUSEL_ORDER.length;
  const factionWrapRef = useRef<HTMLDivElement | null>(null);

  const moveFaction = useCallback(
    (dir: number) => {
      setFactionIndex((i) => (i + dir + totalFactions) % totalFactions);
    },
    [totalFactions]
  );

  const goFaction = useCallback((i: number) => {
    setFactionIndex(i);
  }, []);

  const activeFactionId = FACTION_CAROUSEL_ORDER[factionIndex];
  const activeFactionCopy = FACTION_COPY[activeFactionId];

  const factionAutoplayAllowed = useCarouselAutoplayInView(factionWrapRef);

  /** Outer strip: swipe on title / body copy changes faction; hero area uses inner hero carousel. */
  const canFactionPointerDragBegin = useCallback((e: CarouselDragGateEvent) => {
    const t = e.target as HTMLElement | null;
    if (!t) return false;
    return !t.closest(".world-faction-hero");
  }, []);

  useEffect(() => {
    if (!factionAutoplayAllowed || worldTab !== "factions") return;
    const t = window.setInterval(() => moveFaction(1), FACTION_OUTER_INTERVAL_MS);
    return () => window.clearInterval(t);
  }, [factionAutoplayAllowed, moveFaction, worldTab]);

  useCarouselPointerDrag(factionWrapRef, moveFaction, canFactionPointerDragBegin);

  return (
    <section className="world-dfiance-section" aria-labelledby="world-dfiance-heading">
      <p className="section-label" id="world-dfiance-heading">
        The World of Dfiance
      </p>

      <div
        className="gameplay-tabs world-dfiance-tabs"
        role="tablist"
        aria-label="World of Dfiance"
      >
        <button
          type="button"
          className={`gameplay-tab${worldTab === "factions" ? " is-active" : ""}`}
          id="world-tab-factions"
          role="tab"
          aria-selected={worldTab === "factions"}
          aria-controls="world-panel-factions"
          onClick={() => setWorldTab("factions")}
        >
          Factions
        </button>
        <span className="gameplay-tabs-sep" aria-hidden>
          |
        </span>
        <button
          type="button"
          className={`gameplay-tab${worldTab === "story" ? " is-active" : ""}`}
          id="world-tab-story"
          role="tab"
          aria-selected={worldTab === "story"}
          aria-controls="world-panel-story"
          onClick={() => setWorldTab("story")}
        >
          Story
        </button>
      </div>

      <div
        id="world-panel-factions"
        className="gameplay-tab-panel world-tab-panel world-tab-panel--factions"
        role="tabpanel"
        aria-labelledby="world-tab-factions"
        hidden={worldTab !== "factions"}
      >
        <div className="world-subsection world-subsection--factions">
          <div
            className="carousel-wrap carousel-wrap-factions carousel-wrap-grabbable"
            ref={factionWrapRef}
            role="region"
            aria-label="Browse factions"
          >
            <div className="faction-strip-heading">
              <button
                type="button"
                className="carousel-btn prev carousel-btn--faction-strip"
                onClick={() => moveFaction(-1)}
                aria-label="Previous faction"
              >
                &#8249;
              </button>
              <header className="world-faction-meta world-faction-meta--strip">
                <h3 className="world-faction-title">{activeFactionCopy.title}</h3>
                <p className="world-faction-subtitle">{activeFactionCopy.subtitle}</p>
              </header>
              <button
                type="button"
                className="carousel-btn next carousel-btn--faction-strip"
                onClick={() => moveFaction(1)}
                aria-label="Next faction"
              >
                &#8250;
              </button>
            </div>
            <div className="carousel-track carousel-track-factions">
              {FACTION_CAROUSEL_ORDER.map((fid, i) => {
                const copy = FACTION_COPY[fid];
                const heroes = FACTION_HEROES[fid];
                return (
                  <div
                    key={fid}
                    className={`carousel-slide carousel-slide-faction${i === factionIndex ? " is-active" : ""}`}
                  >
                    <div className="world-faction-panel">
                      <header className="world-faction-meta">
                        <h3 className="world-faction-title">{copy.title}</h3>
                        <p className="world-faction-subtitle">{copy.subtitle}</p>
                      </header>
                      <div className="world-faction-hero-stack">
                        <div className="world-faction-hero-col">
                          <div className="world-faction-hero">
                            <FactionHeroCarousel
                              faction={fid}
                              heroes={heroes}
                              active={activeFactionId === fid}
                              intervalMs={HERO_INTERVAL_MS}
                              ariaLabel={`${copy.title} — heroes`}
                            />
                          </div>
                        </div>
                        <div className="world-faction-hero-bottom-accent" aria-hidden />
                      </div>
                      <div className="world-faction-desc">
                        <p>{copy.description}</p>
                        <ul className="world-faction-strengths">
                          {copy.strengths.map((line, si) => (
                            <li key={si}>{line}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <button
              type="button"
              className="carousel-btn prev carousel-btn--faction-overlay"
              onClick={() => moveFaction(-1)}
              aria-label="Previous faction"
            >
              &#8249;
            </button>
            <button
              type="button"
              className="carousel-btn next carousel-btn--faction-overlay"
              onClick={() => moveFaction(1)}
              aria-label="Next faction"
            >
              &#8250;
            </button>
          </div>
          <div className="carousel-dots">
            {FACTION_CAROUSEL_ORDER.map((fid, i) => (
              <button
                key={fid}
                type="button"
                className={`carousel-dot${i === factionIndex ? " active" : ""}`}
                onClick={() => goFaction(i)}
                aria-label={`Go to ${FACTION_COPY[fid].title}`}
                aria-current={i === factionIndex}
              />
            ))}
          </div>
        </div>
      </div>

      <div
        id="world-panel-story"
        className="gameplay-tab-panel world-tab-panel world-tab-panel--story"
        role="tabpanel"
        aria-labelledby="world-tab-story"
        hidden={worldTab !== "story"}
      >
        <div className="world-story-panel">
          {WORLD_STORY_PARAGRAPHS.map((text, i) => (
            <p key={i}>{text}</p>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WorldOfDfianceSection;
